import React, { useEffect, useState } from "react";
import Select from "react-select";
import clsx from "clsx";
import { GetLocationsType } from "../../../models/_publicCall";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import SimpleMobileStepper from '../../Stepper/Stepper';
import { InstallationJobStepsList } from '../InstallationSteps';
import { Tooltip } from "react-tooltip";

interface LocationSelectorProps {
  locationDB: GetLocationsType[];
  onLocationChange: (locationId: string) => void;
  setCurrentStep: (step: number) => void;
  setStoredData: (data: any) => void;
  InstalltionFormSteps: any;
  jobStatus: string;
  installationJobDB: any;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  locationDB,
  onLocationChange,
  setCurrentStep,
  InstalltionFormSteps,
  jobStatus,
  installationJobDB,
  setStoredData,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<GetLocationsType | null>(null);
  const [address, setAddress] = useState(""); // Address state
  const [error, setError] = useState("");
  const [workOrderPdf, setWorkOrderPdf] = useState("");
  // Load from localStorage first; hydrate from DB only if nothing is saved yet
  useEffect(() => {
    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
    );

    if (storedData.locationSelection?.id) {
      const loc = locationDB.find((l) => l.id === storedData.locationSelection.id);
      if (loc) {
        setSelectedLocation(loc);
        setAddress(storedData.locationSelection.address || loc.address || "");
        setWorkOrderPdf(loc.workOrderPdf || "");
        return;
      }
    }

    // 2) If no local selection yet, seed from DB (only when not completed)
    if (jobStatus !== "Completed" && installationJobDB?.locationId) {
      const loc = locationDB.find((l) => l.id === installationJobDB.locationId);
      if (loc) {
        setSelectedLocation(loc);
        setAddress(installationJobDB.locationAddress || loc.address || "");
        setWorkOrderPdf(loc.workOrderPdf || "");
        const updatedData = {
          ...storedData,
          locationSelection: {
            id: loc.id,
            name: loc.name,
            address: installationJobDB.locationAddress || loc.address || "",
            workOrderPdf: loc.workOrderPdf || "",
            locationNumber: loc.location_number || "",
            headerType: loc.header_type || "",
            contractOwner: loc.contract_owner_id || "",
            videoUuid: loc.video_for_header || "",
            simCardLast4digits: loc.sim_card_last_4_digits || "",
            hasSmoodiRouter: !!loc.has_smoodi_router,
            hasWifiExtender: !!loc.has_wifi_extender,
            hasPressurePump: !!loc.has_pressure_pump,
            hasPressureTank: !!loc.has_pressure_tank,
          },
          locationInformation: {
            headerType: loc.header_type || "",
            contractOwner: loc.contract_owner_id || "",
            videoUuid: loc.video_for_header || "",
            simCardLast4digits: loc.sim_card_last_4_digits || "",
            hasSmoodiRouter: !!loc.has_smoodi_router,
            hasWifiExtender: !!loc.has_wifi_extender,
            hasPressurePump: !!loc.has_pressure_pump,
            hasPressureTank: !!loc.has_pressure_tank,
            flavorPrices: loc.flavor_prices?.map((fp: any) => ({
              flavor: fp.flavor,
              price: String(fp.price ?? 0),
              is_active: fp.is_active,
            })) || [],
          }
        };
        localStorage.setItem(
          INSTALLATION_JOB_STORAGE_KEY,
          JSON.stringify(updatedData)
        );
      }
    }
  }, [locationDB, jobStatus, installationJobDB]);

  // Store in localStorage whenever selectedLocation or address changes
  useEffect(() => {
    if (selectedLocation) {
      const storedData = JSON.parse(
        localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}"
      );

      const updatedData = {
        ...storedData,
        locationSelection: {
          id: selectedLocation.id,
          name: selectedLocation.name,
          address: address, // always store current address
          workOrderPdf: workOrderPdf,
          locationNumber: selectedLocation.location_number || "",
          headerType: selectedLocation.header_type || "",
          contractOwner: selectedLocation.contract_owner_id || "",
          videoUuid: selectedLocation.video_for_header || "",
          simCardLast4digits: selectedLocation.sim_card_last_4_digits || "",
          hasSmoodiRouter: !!selectedLocation.has_smoodi_router,
          hasWifiExtender: !!selectedLocation.has_wifi_extender,
          hasPressurePump: !!selectedLocation.has_pressure_pump,
          hasPressureTank: !!selectedLocation.has_pressure_tank
        },
        locationInformation: {
          headerType: selectedLocation.header_type || "",
          contractOwner: selectedLocation.contract_owner_id || "",
          videoUuid: selectedLocation.video_for_header || "",
          simCardLast4digits: selectedLocation.sim_card_last_4_digits || "",
          hasSmoodiRouter: !!selectedLocation.has_smoodi_router,
          hasWifiExtender: !!selectedLocation.has_wifi_extender,
          hasPressurePump: !!selectedLocation.has_pressure_pump,
          hasPressureTank: !!selectedLocation.has_pressure_tank,
          flavorPrices: selectedLocation.flavor_prices?.map((fp: any) => ({
            flavor: fp.flavor,
            price: String(fp.price ?? 0),
            is_active: fp.is_active,
          })) || [],
        }
      };

      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify(updatedData)
      );
      setStoredData(updatedData);
    }
  }, [selectedLocation, address, workOrderPdf]);


  const handleNext = () => {
    if (!selectedLocation) {
      setError("Please select a location to proceed.");
      return;
    }
    setError("");
    setCurrentStep(InstalltionFormSteps.MACHINE_SETUP);
  };

  return (
    <div>
      <header className="header">
        <h2>Location Selection</h2>
      </header>

      <div className="location-wrapper">
        {/* Location Dropdown */}
        <div className="col-12 mb-4 mt-8">
          <div className="row align-items-center">
            <div className="col-12 col-md-4">
              <label className="wo-label fs-5 fw-bold choose-location-label">Choose Location:</label>
              <Tooltip anchorSelect=".choose-location-label" data-placement="top">
                Select the location where the machine will be installed
              </Tooltip>
            </div>
            <div className="col-12 col-md-8">
              <Select
                isSearchable
                className={clsx("react-select")}
                classNamePrefix="my-react-select"
                placeholder="Select Location"
                options={locationDB.map((loc) => ({
                  value: loc.id,
                  label: loc.name,
                }))}
                value={
                  selectedLocation
                    ? { value: selectedLocation.id, label: selectedLocation.name }
                    : null
                }
                onChange={(option: any) => {
                  const loc = locationDB.find((l) => l.id === option.value);
                  if (loc) {
                    setSelectedLocation(loc);
                    setAddress(loc.address || ""); // pre-populate address
                    onLocationChange(loc.id);
                    setWorkOrderPdf(loc.workOrderPdf || "");
                    setError("");
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Address */}
        {selectedLocation && (
          <div className="col-12 mb-4">
            <div className="row">
              <div className="col-12 col-md-4">
                <label className="wo-label fs-5 fw-bold address-label">Address <span className="text-danger">*</span>:</label>
                <Tooltip anchorSelect=".address-label" data-placement="top">
                  Physical address of the selected location
                </Tooltip>
              </div>
              <div className="col-12 col-md-8">
                <textarea
                  className="form-control"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  name="locationAddress"
                  readOnly={true}
                />

              </div>
            </div>
          </div>
        )}
        {selectedLocation && workOrderPdf && (() => {
          const fileName = workOrderPdf
            ? decodeURIComponent(new URL(workOrderPdf).pathname.split("/").pop() || "")
            : "";

          return (
            <div className="col-12 mb-4">
              <div className="row">
                <div className="col-12 col-md-4">
                  <label className="wo-label fs-5 fw-bold">
                    Work Order PDF:
                  </label>
                </div>
                <div className="col-12 col-md-8">
                  <a href={workOrderPdf}
                    target="_blank"
                    className="text-primary fw-semibold fs-5"
                    style={{ cursor: "pointer" }}
                  >
                    {fileName}
                  </a>
                </div>
              </div>
            </div>
          );
        })()}

        {error && <small className="text-danger">{error}</small>}
        {/* Next Button */}
        {/* <div className="next-btn-container">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!selectedLocation}
            onClick={handleNext}
          >
            Next <i className="fa fa-long-arrow-right" aria-hidden="true"></i>
          </button>
        </div> */}
      </div>
      <SimpleMobileStepper
        totalSteps={3}
        handleNext={handleNext}
        handleBack={() => { }}
        isLastStep={false}
        activeStep={InstallationJobStepsList.findIndex((s) => s.id === InstalltionFormSteps.CHOOSE_LOCATION)}
      />
    </div>
  );
};


export default LocationSelector;
